/* AR Showroom — core: drawers, header, menu, tabs, search, validation. No dependencies.
   Shopify stays authoritative: every price, stock state and cart total shown after an
   interaction is rendered by Shopify (Section Rendering API / Ajax Cart API). */
(() => {
  'use strict';
  const AR = window.AR || {};
  const S = AR.strings || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const store = {
    get(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* storage unavailable */ } }
  };
  const root = (AR.routes && AR.routes.root) || '/';
  const url = (p) => (root.endsWith('/') ? root.slice(0, -1) : root) + p;

  /* ---------------- Toasts ---------------- */
  function toast(msg, opts = {}) {
    const region = $('[data-toasts]');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast' + (opts.error ? ' toast--error' : '');
    el.setAttribute('role', opts.error ? 'alert' : 'status');
    const span = document.createElement('span');
    span.textContent = msg;
    el.appendChild(span);
    if (opts.action) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = opts.action.label;
      b.addEventListener('click', () => { opts.action.run(); el.remove(); });
      el.appendChild(b);
    }
    region.appendChild(el);
    setTimeout(() => el.remove(), opts.action ? 7000 : 3800);
  }

  /* ---------------- Dialogs (drawers) ---------------- */
  function openDialog(id, opener) {
    const d = document.getElementById(id);
    if (!d || typeof d.showModal !== 'function') return false;
    if (!d.open) {
      d.showModal();
      document.documentElement.classList.add('no-scroll');
      d.dispatchEvent(new CustomEvent('ar:open', { detail: { opener } }));
    }
    return true;
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const id = opener.getAttribute('data-open');
      if (id === 'cart-drawer' && AR.cartType === 'page') return;
      if (opener.disabled) return;
      if (openDialog(id, opener)) e.preventDefault();
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) { const d = closer.closest('dialog'); if (d) d.close(); return; }
    if (e.target.tagName === 'DIALOG' && e.target.open) {
      const r = e.target.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) e.target.close();
    }
  });
  document.addEventListener('close', (e) => {
    if (e.target.tagName === 'DIALOG' && !$$('dialog[open]').length) document.documentElement.classList.remove('no-scroll');
  }, true);

  /* ---------------- Header ---------------- */
  const header = $('[data-header]');
  if (header) {
    const setH = () => document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
    setH();
    if ('ResizeObserver' in window) new ResizeObserver(setH).observe(header);
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return; ticking = true;
      requestAnimationFrame(() => { header.classList.toggle('is-scrolled', window.scrollY > 8); ticking = false; });
    }, { passive: true });

    const btns = $$('[data-mega-btn]', header);
    const closeAll = (except) => btns.forEach((b) => {
      if (b === except) return;
      b.setAttribute('aria-expanded', 'false');
      const p = document.getElementById(b.getAttribute('aria-controls')); if (p) p.hidden = true;
    });
    const toggle = (b, force) => {
      const open = force !== undefined ? force : b.getAttribute('aria-expanded') !== 'true';
      closeAll(b);
      b.setAttribute('aria-expanded', String(open));
      const p = document.getElementById(b.getAttribute('aria-controls')); if (p) p.hidden = !open;
    };
    const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    btns.forEach((b) => {
      const li = b.closest('li');
      let t;
      b.addEventListener('click', () => toggle(b));
      if (hoverable) {
        li.addEventListener('mouseenter', () => { clearTimeout(t); t = setTimeout(() => toggle(b, true), 90); });
        li.addEventListener('mouseleave', () => { clearTimeout(t); t = setTimeout(() => toggle(b, false), 180); });
      }
      li.addEventListener('keydown', (e) => { if (e.key === 'Escape' && b.getAttribute('aria-expanded') === 'true') { toggle(b, false); b.focus(); } });
      li.addEventListener('focusout', (e) => { if (!li.contains(e.relatedTarget)) toggle(b, false); });
    });
    document.addEventListener('click', (e) => { if (!e.target.closest('.nav__item')) closeAll(); });
  }

  /* Mobile menu accordion */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-acc]');
    if (!b) return;
    const open = b.getAttribute('aria-expanded') !== 'true';
    b.setAttribute('aria-expanded', String(open));
    const p = document.getElementById(b.getAttribute('aria-controls')); if (p) p.hidden = !open;
  });

  /* ---------------- Tabs (stage + shelves) ---------------- */
  function initTabs(scope) {
    $$('[data-tabs]', scope).forEach((list) => {
      if (list.dataset.ready) return; list.dataset.ready = '1';
      const tabs = $$('[role="tab"]', list);
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const p = document.getElementById(t.getAttribute('aria-controls'));
          if (!p) return;
          p.hidden = !on;
          if (on && !reduceMotion) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); }
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (e) => {
          let n = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') n = tabs[0];
          if (e.key === 'End') n = tabs[tabs.length - 1];
          if (n) { e.preventDefault(); select(n, true); }
        });
      });
    });
  }
  initTabs(document);
  document.addEventListener('shopify:section:load', (e) => { initTabs(e.target); if (window.ARX.syncAll) window.ARX.syncAll(); });

  /* ---------------- Predictive search ---------------- */
  $$('form[data-predictive]').forEach((form) => {
    const input = $('input[type="search"]', form);
    const panel = $('[data-panel]', form);
    const clear = $('[data-clear]', form);
    const status = $('[data-search-status]', form);
    let ctrl; let active = -1;
    const options = () => $$('[role="option"]', panel);
    const close = () => { panel.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; };
    const highlight = (i) => {
      const opts = options(); if (!opts.length) return;
      active = (i + opts.length) % opts.length;
      opts.forEach((o, k) => o.setAttribute('aria-selected', String(k === active)));
      input.setAttribute('aria-activedescendant', opts[active].id);
      opts[active].scrollIntoView({ block: 'nearest' });
    };
    const run = debounce(async () => {
      const q = input.value.trim();
      clear.hidden = !q;
      if (q.length < 2) { close(); return; }
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      try {
        const res = await fetch(`${AR.routes.suggest}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,query,page&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last`, { signal: ctrl.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parse(await res.text());
        const sec = doc.getElementById('shopify-section-predictive-search') || doc.body;
        panel.innerHTML = sec.innerHTML;
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        active = -1;
        const results = $('[data-ps-results]', panel);
        if (status && results) status.textContent = `${results.dataset.count || 0} products found`;
      } catch (err) { if (err.name !== 'AbortError') close(); }
    }, 220);
    input.addEventListener('input', run);
    input.addEventListener('focus', () => { if (panel.innerHTML.trim() && input.value.trim().length > 1) { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); } });
    input.addEventListener('keydown', (e) => {
      if (panel.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); }
      else if (e.key === 'Escape') { close(); }
      else if (e.key === 'Enter' && active > -1) { const a = $('a', options()[active]); if (a) { e.preventDefault(); window.location.href = a.href; } }
    });
    clear.addEventListener('click', () => { input.value = ''; clear.hidden = true; close(); input.focus(); });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) close(); });
    form.addEventListener('focusout', (e) => { if (!form.contains(e.relatedTarget)) setTimeout(close, 120); });
  });

  /* ---------------- Form validation ---------------- */
  function validate(form) {
    let first = null;
    $$('input, textarea, select', form).forEach((f) => {
      if (f.type === 'hidden' || f.disabled) return;
      const errId = f.id ? `${f.id}-err` : '';
      let msg = '';
      if (f.required && !f.value.trim()) msg = `Enter your ${(f.dataset.label || f.name || 'details').toLowerCase()}.`;
      else if (f.type === 'email' && f.value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.value.trim())) msg = 'Enter an email address like name@example.com.';
      let box = errId ? document.getElementById(errId) : null;
      if (msg) {
        f.setAttribute('aria-invalid', 'true');
        if (!box && errId) { box = document.createElement('p'); box.className = 'field__error'; box.id = errId; f.insertAdjacentElement('afterend', box); }
        if (box) { box.textContent = msg; f.setAttribute('aria-describedby', errId); }
        if (!first) first = f;
      } else {
        f.removeAttribute('aria-invalid');
        if (box) box.remove();
      }
    });
    if (first) first.focus();
    return !first;
  }
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form.matches('[data-validate-form], #footer-newsletter') && !validate(form)) e.preventDefault();
  }, true);

  /* ---------------- Stage intro: run once ---------------- */
  $$('.stage[data-intro]').forEach((s) => setTimeout(() => s.removeAttribute('data-intro'), 1400));

  window.ARX = { $, $$, debounce, parse, store, url, toast, openDialog, reduceMotion, S, AR };
})();